import { useCallback, useState } from 'react';
import { useApiClientsOrNull } from '../../../context/ApiClientsContext';
import { fetchPermissionList as fetchPermissionListApi, getSphereById } from '@center-platform/spheres/sphere';
import type { PermissionList } from 'types/permissions';
import type { Sphere as GlobalSphere } from 'types/global';
import type { Task } from '../../molecules/TaskList/TaskListMolecule.types';
import type { SphereChange } from '../../../hooks/useSphereChangeEffect';

export type FetchPermissionList = (
  sphereId: string,
  nodeLabel: string,
  permissions?: string[]
) => Promise<PermissionList | null>;

export interface EditSphereLauncherState {
  sphereToEdit: GlobalSphere | null;
  visible: boolean;
  onEditSphere: (taskId: string, task: Task) => Promise<void>;
  fetchPermissionList: FetchPermissionList;
  onSave: (updatedSphere: GlobalSphere) => Promise<void>;
  /** The sphere changed while the editor stays open (its glyph, colour or picture). */
  onSphereRefresh: (sphereId: string) => void;
  onClose: () => void;
  /**
   * The last sphere this editor changed, by a save or while open. Hosts pass it to the views behind
   * the editor (task lists, the toolbar's task panel) so they re-read it.
   */
  sphereChange: SphereChange | null;
}

/**
 * Ported from `MetaspaceGrid`'s inline `handleEditSphere`/`fetchPermissionListHandler` state so both
 * the legacy grid and `SpaceHost` open the same dialog through the same fetch/permission path
 * (spec § Phase R4 task 1).
 */
export function useEditSphereLauncher(): EditSphereLauncherState {
  const apiClients = useApiClientsOrNull();
  const [visible, setVisible] = useState(false);
  const [sphereToEdit, setSphereToEdit] = useState<GlobalSphere | null>(null);
  const [sphereChange, setSphereChange] = useState<SphereChange | null>(null);

  const onSphereRefresh = useCallback((sphereId: string) => {
    setSphereChange((prev) => ({ sphereId, revision: (prev?.revision ?? 0) + 1 }));
  }, []);

  const fetchPermissionList = useCallback<FetchPermissionList>(
    async (sphereId, nodeLabel, permissions) => {
      if (!apiClients?.permissionClient) {
        console.error('[EditSphereLauncher] Permission client not available');
        return null;
      }
      if (!sphereId || !nodeLabel) {
        console.error('[EditSphereLauncher] Sphere ID and node label required');
        return null;
      }

      try {
        return await fetchPermissionListApi(apiClients.permissionClient, {
          resourceType: nodeLabel,
          resourceId: sphereId,
          permissions,
        });
      } catch (err) {
        console.error('[EditSphereLauncher] Error fetching permission list:', err);
        return null;
      }
    },
    [apiClients]
  );

  const onEditSphere = useCallback(
    async (_taskId: string, task: Task) => {
      if (!apiClients?.graphClient) {
        console.error('[EditSphereLauncher] Graph client not available');
        return;
      }

      // Use the task's sphereId, not the taskId (which for virtual tasks is "virtual-xxx-timestamp")
      const targetSphereId = task.sphereId;
      if (!targetSphereId) {
        console.error('[EditSphereLauncher] Task has no sphereId:', task);
        return;
      }

      try {
        // Deliberate exception to api-clients.md Rule 6 (reads go through
        // `globalSphereCache.getSphereData`): the edit form needs the FULL sphere with assigned
        // perspectives, uncached, and the global cache serves neither — it returns the basic
        // projection and would hand back a stale copy of the very record about to be edited.
        const response = await getSphereById(
          apiClients.graphClient,
          targetSphereId,
          true, // getAssignedPerspectives
          true // skipCache — fetch fresh so EditSphere doesn't need to re-fetch
        );

        const sphereData = response.data?.sphere as GlobalSphere;
        if (!sphereData) {
          console.error('[EditSphereLauncher] Sphere not found:', targetSphereId);
          return;
        }

        setSphereToEdit(sphereData);
        setVisible(true);
      } catch (err) {
        console.error('[EditSphereLauncher] Error fetching sphere for edit:', err);
      }
    },
    [apiClients]
  );

  const onSave = useCallback(
    (updatedSphere: GlobalSphere): Promise<void> => {
      setVisible(false);
      setSphereToEdit(null);
      if (updatedSphere?.id) onSphereRefresh(updatedSphere.id);
      return Promise.resolve();
    },
    [onSphereRefresh]
  );

  const onClose = useCallback(() => {
    setVisible(false);
    setSphereToEdit(null);
  }, []);

  return { sphereToEdit, visible, onEditSphere, fetchPermissionList, onSave, onSphereRefresh, onClose, sphereChange };
}
